import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Menu, X, MessageSquare, Globe } from "lucide-react";
import { resolveNavLinks } from "../../lib/navUtils";
import { useLang } from "../../contexts/LangContext";
import { PLACEHOLDER_INSTAGRAM, placeholder, ARIA_SCROLL_TOP, ARIA_LANG, ARIA_MENU } from "../../lib/constants";

export default function Header({ navigation, globals, hasServices = true, hasPortfolio = true, hasTestimonials = true, hasBlog = true, enabledSections, uiStrings, contactHref, forceDark = false }) {
  const { lang, setLang, supportedLangs } = useLang();
  const location = useLocation();
  const isLanding = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openLang, setOpenLang] = useState(false);

  const navLinks = resolveNavLinks(navigation, enabledSections, { services: hasServices, portfolio: hasPortfolio, testimonials: hasTestimonials, blog: hasBlog });
  function resolveHref(href) { return href.startsWith("#") && !isLanding ? "/" + href : href; }
  const logoText = uiStrings?.general?.logo || globals?.logo || placeholder("short");
  const logoSubtitle = uiStrings?.general?.subtitle || globals?.subtitle;
  const ctaLabel = uiStrings?.general?.cta_label || globals?.cta_label || placeholder("short");
  const instagramUrl = globals?.instagram_url || PLACEHOLDER_INSTAGRAM;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) setOpenLang(false);
  }, [open]);

  useEffect(() => {
    if (openLang) setOpenLang(false);
  }, [scrolled]);

  return (
    <header data-id="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-700 ${
        scrolled || forceDark
          ? "bg-offwhite/80 backdrop-blur-md border-b border-fendi/60 py-0 shadow-sm"
          : "bg-transparent py-2"
      }`}
    >
      <div data-id="header-container" className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        {isLanding ? (
          <button
            data-id="logo-link"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            aria-label={ARIA_SCROLL_TOP}
            className="group bg-transparent border-none cursor-pointer p-0 py-2"
          >
            <LogoInner logoText={logoText} logoSubtitle={logoSubtitle} scrolled={scrolled} forceDark={forceDark} />
          </button>
        ) : (
          <Link
            to="/"
            data-id="logo-link"
            aria-label={ARIA_SCROLL_TOP}
            className="group bg-transparent border-none cursor-pointer p-0 py-2 no-underline"
          >
            <LogoInner logoText={logoText} logoSubtitle={logoSubtitle} scrolled={scrolled} forceDark={forceDark} />
          </Link>
        )}

        <nav data-id="desktop-nav" className="hidden lg:flex items-center gap-10">
          {navLinks.map((l, i) => (
            <a
              key={l.label || i}
              href={resolveHref(l.href)}
              data-id={`nav-${l.label.toLowerCase()}`}
              className={`link-underline text-[13px] font-medium tracking-wide transition-colors duration-500 ${
                scrolled || forceDark
                  ? "text-softblack/80 hover:text-softblack" 
                  : "text-white/85 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
          
          <div data-id="desktop-lang-wrapper" className="hidden lg:relative lg:flex">
            <button data-id="desktop-lang-button"
              onClick={() => setOpenLang((v) => !v)}
              className={`flex items-center justify-center w-9 h-9 rounded-full border transition-all duration-500 ${
                openLang
                  ? "border-champagne text-champagne bg-champagne/5"
                  : scrolled || forceDark
                    ? "border-fendi/40 text-softblack/70 hover:border-champagne hover:text-champagne hover:bg-champagne/5"
                    : "border-white/20 text-white/70 hover:border-white/50 hover:text-white hover:bg-white/10"
              }`}
              aria-label={ARIA_LANG}
            >
              <Globe data-id="desktop-lang-globe" size={16} />
            </button>
            {openLang && (
              <div data-id="desktop-lang-dropdown" className="absolute right-0 top-full mt-2 bg-offwhite border border-fendi/30 rounded-lg shadow-xl py-2 min-w-[160px] z-50">
                {supportedLangs.map((l) => (
                  <button
                    key={l.code}
                    data-id={`desktop-lang-option-${l.code}`}
                    onClick={() => { setLang(l.code); setOpenLang(false); }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold tracking-widest transition-all flex items-center gap-2 ${
                      lang === l.code
                        ? "text-champagne bg-champagne/5"
                        : "text-warmgray hover:text-softblack hover:bg-fendi/10"
                    }`}
                  >
                    {l.flag || l.code.split("-")[0].toLowerCase()} {l.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a href={instagramUrl} target="_blank" rel="noopener noreferrer" data-id="instagram-header" className={`hidden lg:flex items-center justify-center w-9 h-9 rounded-full border transition-all duration-500 ${scrolled || forceDark ? "border-fendi/40 text-softblack/70 hover:border-champagne hover:text-champagne hover:bg-champagne/5" : "border-white/20 text-white/70 hover:border-white/50 hover:text-white hover:bg-white/10"}`}>
            <svg data-id="instagram-header-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
            </svg>
          </a>
        </nav>

        <div data-id="header-right-actions" className="flex items-center gap-2 lg:gap-3 lg:ml-8">
          <a href={contactHref || "#contato"} data-id="cta-header-schedule" className={`!hidden lg:!inline-flex btn-primary !px-5 lg:!px-6 !py-3 !text-[10px] shadow-lg transition-all duration-500 ${!scrolled && !forceDark && "border-white/20 hover:bg-white hover:text-softblack"}`}>
            {ctaLabel}
          </a>
          <a href={instagramUrl} target="_blank" rel="noopener noreferrer" data-id="instagram-header-mobile" className={`lg:hidden flex items-center justify-center w-9 h-9 rounded-full border transition-all duration-500 ${scrolled || forceDark ? "border-fendi/40 text-softblack/70" : "border-white/40 text-white bg-white/10"}`}>
            <svg data-id="instagram-header-mobile-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
            </svg>
          </a>
          <div data-id="mobile-lang-wrapper" className="lg:hidden relative">
            <button data-id="mobile-lang-button"
              onClick={() => setOpenLang((v) => !v)}
              className={`flex items-center justify-center w-9 h-9 rounded-full border transition-all duration-500 ${scrolled || forceDark ? "border-fendi/40 text-softblack/70" : "border-white/40 text-white bg-white/10"}`}
              aria-label={ARIA_LANG}
            >
              <Globe data-id="mobile-lang-globe" size={16} />
            </button>
            {openLang && (
              <div data-id="mobile-lang-dropdown" className="absolute right-0 top-full mt-2 bg-offwhite border border-fendi/30 rounded-lg shadow-xl py-2 min-w-[160px] z-50">
                {supportedLangs.map((l) => (
                  <button
                    key={l.code}
                    data-id={`mobile-lang-option-${l.code}`}
                    onClick={() => { setLang(l.code); setOpenLang(false); }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold tracking-widest transition-all flex items-center gap-2 ${
                      lang === l.code
                        ? "text-champagne bg-champagne/5"
                        : "text-warmgray hover:text-softblack hover:bg-fendi/10"
                    }`}
                  >
                    {l.flag || l.code.split("-")[0].toLowerCase()} {l.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <a href={contactHref || "#contato"} data-id="cta-header-mobile" className={`lg:hidden flex items-center justify-center w-9 h-9 rounded-full border transition-all duration-500 ${scrolled || forceDark ? "border-fendi/40 text-softblack/70" : "border-white/40 text-white bg-white/10"}`}>
            <MessageSquare data-id="cta-header-mobile-icon" size={16} />
          </a>
          <button
            data-id="mobile-menu-toggle"
            className={`lg:hidden p-2 transition-colors duration-500 ${
              scrolled || forceDark
                ? "text-softblack"
                : "text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]"
            }`}
            onClick={() => setOpen((v) => !v)}
            aria-label={ARIA_MENU}
          >
            {open ? <X data-id="mobile-menu-icon-close" size={22} /> : <Menu data-id="mobile-menu-icon-open" size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-id="mobile-menu-panel" className="lg:hidden border-t border-fendi/60 bg-offwhite/95 backdrop-blur-md animate-in slide-in-from-top-2 duration-300">
          <div data-id="mobile-menu-padding" className="px-6 py-8 flex flex-col gap-6">
            <div data-id="mobile-nav-links" className="flex flex-col gap-4">
              {navLinks.length > 0 ? navLinks.map((l) => (
                <a
                  key={l.label || i}
                  href={resolveHref(l.href)}
                  onClick={() => setOpen(false)}
                  data-id={`mobile-nav-${l.label.toLowerCase()}`}
                  className="text-softblack/85 text-sm font-medium tracking-wide py-2 border-b border-fendi/20"
                >
                  {l.label}
                </a>
              )) : (
                <a href="/" onClick={() => setOpen(false)} className="text-softblack/85 text-sm font-medium tracking-wide py-2 border-b border-fendi/20">
                  Início
                </a>
              )}
            </div>
            
            <div data-id="mobile-lang-section" className="pt-4 border-t border-fendi/10">
              <p data-id="mobile-lang-heading" className="text-[9px] uppercase tracking-[0.2em] text-warmgray mb-4 flex items-center gap-2">
                <Globe data-id="mobile-lang-heading-globe" size={12} /> Idioma
              </p>
              <div data-id="mobile-lang-buttons" className="flex gap-4">
                {supportedLangs.map((l) => (
                  <button
                    key={l.code}
                    data-id={`mobile-lang-btn-${l.code}`}
                    onClick={() => { setLang(l.code); setOpen(false); }}
                    className={`px-4 py-2 text-xs font-bold tracking-widest border transition-all ${
                      lang === l.code
                        ? "bg-softblack text-white border-softblack"
                        : "text-warmgray border-fendi/30"
                    }`}
                  >
                    {l.flag || l.code.split("-")[0].toLowerCase()} {l.name || l.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function LogoInner({ logoText, logoSubtitle, scrolled, forceDark }) {
  return (
    <div data-id="logo-inner" className="flex items-baseline gap-2">
      <span data-id="logo-text" className={`font-serif text-2xl tracking-tight transition-colors duration-500 ${scrolled || forceDark ? "text-softblack" : "text-white"}`}>
        {logoText}<span data-id="logo-dot" className="text-champagne">.</span>
      </span>
      {logoSubtitle && (
        <span data-id="logo-subtitle" className={`hidden sm:inline-block text-[10px] tracking-[0.34em] uppercase font-medium transition-colors duration-500 ${scrolled || forceDark ? "text-softblack/80" : "text-white/85"}`}>
          {logoSubtitle}
        </span>
      )}
    </div>
  );
}
